import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Heart, History, Plus, Minus } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Guide = () => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const shortBio = t.guide.bio_short;
  const restBio = t.guide.bio_rest;

  return (
    <section 
      className="py-24 md:py-32 bg-[#1C1917] text-white relative overflow-hidden"
      data-testid="guide-section"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_history-on-wheels/artifacts/l5jfzirq_Carlos%20Casal.png"
                alt="Carlos Casal - Guide"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#C2410C] rounded-2xl -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 border-2 border-[#F59E0B] rounded-2xl -z-10" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] mb-4 font-semibold">
              {t.guide.title}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mb-2">
              {t.guide.name}
            </h2>
            <p className="text-white/60 mb-6">{t.guide.age}</p>

            {/* Role badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-8">
              <GraduationCap className="h-5 w-5 text-[#F59E0B]" />
              <span className="text-sm">{t.guide.role}</span>
            </div>

            {/* Bio - expandable */}
            <div className="mb-8">
              <p className="text-white/80 text-lg leading-relaxed inline">
                {shortBio}
              </p>
              {restBio && !expanded && (
                <button
                  onClick={() => setExpanded(true)}
                  className="inline-flex items-center gap-1 ml-2 text-[#F59E0B] hover:text-[#C2410C] transition-colors"
                  data-testid="guide-expand-bio-btn"
                >
                  <Plus className="h-5 w-5" />
                </button>
              )}
              <AnimatePresence>
                {expanded && restBio && (
                  <motion.span
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="block mt-4"
                  >
                    <p className="text-white/80 text-lg leading-relaxed inline">
                      {restBio}
                    </p>
                    <button
                      onClick={() => setExpanded(false)}
                      className="inline-flex items-center gap-1 ml-2 text-[#F59E0B] hover:text-[#C2410C] transition-colors"
                      data-testid="guide-collapse-bio-btn"
                    >
                      <Minus className="h-5 w-5" />
                    </button>
                  </motion.span>
                )}
              </AnimatePresence>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C2410C]/20 flex items-center justify-center flex-shrink-0">
                  <Heart className="h-5 w-5 text-[#C2410C]" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Pasion</h4>
                  <p className="text-sm text-white/60">Por la historia de Madrid</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                  <History className="h-5 w-5 text-[#F59E0B]" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Conocimiento</h4>
                  <p className="text-sm text-white/60">Historiador experto</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Guide;
